import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, HelpCircle, CheckCircle2 } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FinalCTA } from "@/components/home/HomeSections";
import {
  TOPIC_LANDINGS,
  getTopicLanding,
  getArticlesByCategory,
  type TopicLanding,
} from "@/lib/learningHub";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";

export const Route = createFileRoute("/learn/topic/$topicSlug")({
  loader: ({ params }) => {
    const topic = getTopicLanding(params.topicSlug);
    if (!topic) throw notFound();
    return { topic };
  },
  head: ({ loaderData }) => {
    const topic = loaderData?.topic;
    if (!topic) {
      return {
        meta: [
          { title: "Topic not found | Colorado Web Impressions Learn (Blog)" },
        ],
      };
    }
    const url = `${SITE_URL}/learn/topic/${topic.slug}`;
    return {
      meta: [
        { title: topic.metaTitle },
        { name: "description", content: topic.metaDescription },
        { property: "og:title", content: topic.metaTitle },
        { property: "og:description", content: topic.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        buildBreadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Learn", path: "/learn" },
          { name: topic.h1 ?? topic.slug, path: `/learn/topic/${topic.slug}` },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <PageHero
        eyebrow="Not found"
        title="That topic doesn't exist."
        description="Browse all of our topics in the Learn (Blog)."
      />
      <section className="bg-background py-16">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Go to Learn (Blog) <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  ),
  component: TopicLandingPage,
});

function TopicLandingPage() {
  const { topic } = Route.useLoaderData() as { topic: TopicLanding };
  const articles = getArticlesByCategory(topic.category);
  const otherTopics = TOPIC_LANDINGS.filter((t) => t.slug !== topic.slug);

  // Article list schema (lightweight ItemList) helps Google understand the
  // page is a topic hub, not a thin filter view.
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: articles.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${a.slug}`,
      name: a.title,
    })),
  };

  const faqSchema =
    topic.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: topic.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  return (
    <SiteLayout>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <PageHero
        eyebrow={topic.eyebrow}
        title={topic.h1}
        description={topic.intro}
      />

      <section className="bg-background pb-4 pt-8">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Breadcrumbs
            items={[
              { label: "Learn (Blog)", to: "/learn" },
              { label: topic.category },
            ]}
          />
        </div>
      </section>

      {/* Body copy */}
      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:px-8">
          <div>
            <div className="space-y-5 text-lg leading-relaxed text-foreground/85">
              {topic.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {topic.highlights.map((h) => (
                <div
                  key={h}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                  <span className="text-sm font-semibold text-foreground">
                    {h}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                Browse other topics
              </div>
              <ul className="mt-4 space-y-1.5">
                {otherTopics.map((t) => (
                  <li key={t.slug}>
                    <Link
                      to="/learn/topic/$topicSlug"
                      params={{ topicSlug: t.slug }}
                      className="block rounded-md px-3 py-2 text-sm font-semibold text-foreground/80 transition-smooth hover:bg-muted hover:text-brand-gold-deep"
                    >
                      {t.category}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/learn"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold-deep transition-smooth hover:text-foreground"
              >
                All articles <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Article list */}
      <section className="bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              {articles.length} article{articles.length === 1 ? "" : "s"} on{" "}
              {topic.category}
            </h2>
            <Link
              to="/learn"
              className="text-sm font-semibold text-brand-gold-deep transition-smooth hover:text-foreground"
            >
              Browse all topics →
            </Link>
          </div>

          {articles.length === 0 ? (
            <p className="mt-6 text-base text-muted-foreground">
              We&apos;re publishing new {topic.category} articles soon. In the
              meantime,{" "}
              <Link
                to="/learn"
                className="font-semibold text-brand-gold-deep underline-offset-2 hover:underline"
              >
                browse the rest of the Learn (Blog)
              </Link>
              .
            </p>
          ) : (
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((a) => {
                const Icon = a.icon;
                return (
                  <li key={a.slug}>
                    <Link
                      to={a.slug}
                      className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="flex items-center gap-3">
                        <span className="grid h-10 w-10 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className="text-[0.7rem] font-bold uppercase tracking-wider text-brand-gold-deep">
                          {a.read}
                        </span>
                      </div>
                      <h3 className="mt-4 text-lg font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                        {a.title}
                      </h3>
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {a.desc}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold-deep">
                        Read article{" "}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* FAQ */}
      {topic.faqs.length > 0 && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="flex items-center gap-3">
              <HelpCircle className="h-6 w-6 text-brand-gold-deep" />
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                Common questions about {topic.category} in Colorado
              </h2>
            </div>
            <ul className="mt-8 space-y-5">
              {topic.faqs.map((f) => (
                <li
                  key={f.q}
                  className="rounded-xl border border-border bg-card p-6 shadow-card"
                >
                  <h3 className="text-base font-extrabold text-foreground">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                    {f.a}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <FinalCTA />
    </SiteLayout>
  );
}
